/* -------------------------------------------------------------------------
 * Lightweight in-memory request cache (ENG-123032)
 *
 * Imported and used inside makeCancellableAPIRequest when cache:true is set
 * in the options object.
 * -------------------------------------------------------------------------
 */

export type CacheEntry<T = unknown> = {
  state: 'PENDING' | 'FULFILLED';
  promise: Promise<T>;
  subscribers: number;
  abort: () => void;
};

const cacheData: Record<string, CacheEntry> = {};

/** Interval at which FULFILLED entries are swept. */
export const CACHE_INVALIDATION_INTERVAL_MS = 10_000;

export const CacheFactory = {
  has(key: string): boolean {
    return Object.prototype.hasOwnProperty.call(cacheData, key);
  },
  get<T>(key: string): CacheEntry<T> | undefined {
    return cacheData[key] as CacheEntry<T> | undefined;
  },
  /** Inserts an entry and auto-removes it on rejection. */
  save<T>(key: string, entry: CacheEntry<T>): void {
    cacheData[key] = entry as CacheEntry;
    entry.promise.catch(() => {
      if (cacheData[key] === (entry as CacheEntry)) {
        delete cacheData[key];
      }
    });
  },
  /**
   * Without a key: removes only FULFILLED entries (periodic sweep).
   * PENDING entries are left — the request may still have active subscribers.
   * With a key: removes that entry unconditionally.
   */
  invalidate(key?: string): void {
    if (key !== undefined) {
      delete cacheData[key];
      return;
    }
    Object.keys(cacheData).forEach((k) => {
      if (cacheData[k]?.state === 'FULFILLED') delete cacheData[k];
    });
  },
  /** Force-clears every entry regardless of state. Aborts in-flight requests. */
  clearAll(): void {
    Object.keys(cacheData).forEach((k) => {
      if (cacheData[k]?.state === 'PENDING') cacheData[k].abort();
      delete cacheData[k];
    });
  },
};

export const cacheSweepIntervalId = setInterval(
  () => CacheFactory.invalidate(),
  CACHE_INVALIDATION_INTERVAL_MS,
);

/** Stops the background sweep. Call in test teardown or non-browser runtimes. */
export function stopCacheSweep(): void {
  clearInterval(cacheSweepIntervalId);
}
